import { useEffect, useMemo, useRef, useState } from 'react'
import { Chessboard } from 'react-chessboard'
import { motion } from 'framer-motion'

import { Button } from '@/components/common'
import {
  BoardState,
  cloneBoard,
  parseFenPlacement,
  squareToCoords,
  boardToFen,
  validateBoard,
} from '@/utils/board'
import { HistoryStack } from '@/utils/history'

export interface BoardEditorProps {
  initialFen: string
  orientation?: 'white' | 'black'
  boardWidth?: number
  imagePreview?: string | null
  onFenChange?: (fen: string) => void
  onClose?: () => void
}

type Tool = 'move' | 'palette' | 'erase'

const palettePieces = ['K', 'Q', 'R', 'B', 'N', 'P']
const pieceLabelMap: Record<string, string> = {
  K: 'king',
  Q: 'queen',
  R: 'rook',
  B: 'bishop',
  N: 'knight',
  P: 'pawn',
}

// Use react-chessboard's built-in piece rendering
// Pieces are rendered by displaying a mini chessboard with just that piece

const BoardEditor = ({
  initialFen,
  orientation = 'white',
  boardWidth = 560,
  imagePreview,
  onFenChange,
  onClose,
}: BoardEditorProps) => {
  const initialBoard = useMemo(() => parseFenPlacement(initialFen), [initialFen])
  const historyRef = useRef(
    new HistoryStack<BoardState>(initialBoard, cloneBoard, 100)
  )

  const [board, setBoard] = useState<BoardState>(initialBoard)
  const [activeTool, setActiveTool] = useState<Tool>('move')
  const [selectedSquare, setSelectedSquare] = useState<string | null>(null)
  const [palettePiece, setPalettePiece] = useState<string>('P')
  const [validatePosition, setValidatePosition] = useState(true)

  useEffect(() => {
    historyRef.current.reset(initialBoard)
    setBoard(initialBoard)
    setSelectedSquare(null)
    setActiveTool('move')
    setPalettePiece('P')
    onFenChange?.(boardToFen(initialBoard))
  }, [initialBoard, onFenChange])

  const currentFen = useMemo(() => boardToFen(board), [board])
  const validation = useMemo(
    () => (validatePosition ? validateBoard(board) : { valid: true, messages: [] }),
    [board, validatePosition]
  )

  useEffect(() => {
    onFenChange?.(currentFen)
  }, [currentFen, onFenChange])

  const handleCommit = (nextBoard: BoardState) => {
    historyRef.current.push(nextBoard)
    setBoard(nextBoard)
  }

  const handleUndo = () => {
    const previous = historyRef.current.undo()
    if (previous) {
      setBoard(previous)
      setSelectedSquare(null)
    }
  }

  const handleRedo = () => {
    const next = historyRef.current.redo()
    if (next) {
      setBoard(next)
      setSelectedSquare(null)
    }
  }

  const handleReset = () => {
    historyRef.current.reset(initialBoard)
    setBoard(initialBoard)
    setSelectedSquare(null)
  }

  const handleSquareClick = (square: string) => {
    const coords = squareToCoords(square)
    if (!coords) return

    const nextBoard = cloneBoard(board)

    if (activeTool === 'palette') {
      nextBoard[coords.row][coords.col] = palettePiece
      handleCommit(nextBoard)
      return
    }

    if (activeTool === 'erase') {
      nextBoard[coords.row][coords.col] = null
      handleCommit(nextBoard)
      return
    }

    if (!selectedSquare) {
      if (!board[coords.row][coords.col]) return
      setSelectedSquare(square)
      return
    }

    if (selectedSquare === square) {
      setSelectedSquare(null)
      return
    }

    const origin = squareToCoords(selectedSquare)
    if (!origin) return

    const movingPiece = board[origin.row][origin.col]
    if (!movingPiece) {
      setSelectedSquare(null)
      return
    }

    nextBoard[coords.row][coords.col] = movingPiece
    nextBoard[origin.row][origin.col] = null
    handleCommit(nextBoard)
    setSelectedSquare(null)
  }

  const squareStyles = useMemo(() => {
    if (!selectedSquare) {
      return {}
    }

    return {
      [selectedSquare]: {
        boxShadow: 'inset 0 0 0 3px rgba(253, 230, 138, 0.8)',
      },
    }
  }, [selectedSquare])

  const toolButtonClass = (tool: Tool) =>
    `flex-1 px-3 py-2 border rounded-md text-sm font-semibold transition ${activeTool === tool
      ? 'bg-primary-500 text-white border-primary-500'
      : 'bg-white dark:bg-dark-800 text-gray-700 dark:text-dark-200 border-gray-200 dark:border-dark-700'
    }`

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      className="bg-white dark:bg-dark-900 border border-gray-200 dark:border-dark-700 rounded-2xl shadow-2xl p-6 max-w-5xl mx-auto"
    >
      <div className="flex flex-col gap-2 mb-6">
        <div className="flex items-center justify-between gap-3">
          <div>
            <p className="text-sm font-semibold text-gray-400 uppercase tracking-wider">
              Manual corrections
            </p>
            <h3 className="text-lg font-bold text-gray-900 dark:text-dark-50">
              Edit board
            </h3>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={handleReset}>
              Reset board
            </Button>
            <Button variant="secondary" size="sm" onClick={onClose}>
              Close editor
            </Button>
          </div>
        </div>
        <p className="text-sm text-gray-500 dark:text-dark-400">
          Select squares to move a piece, use the palette to add pieces, or
          erase to clear a square. Undo/redo keep the last 100 edits.
        </p>
      </div>

      <div className="flex flex-col lg:flex-row gap-6">
        {imagePreview && (
          <div className="lg:w-1/3 rounded-xl border border-gray-200 dark:border-dark-700 p-3 bg-gray-50 dark:bg-dark-900">
            <p className="text-xs font-semibold text-gray-500 dark:text-dark-400 uppercase tracking-wider mb-2">
              Original upload
            </p>
            <div className="aspect-video w-full rounded-lg overflow-hidden border border-gray-200 dark:border-dark-700">
              <img
                src={imagePreview}
                alt="Uploaded board reference"
                className="h-full w-full object-contain"
              />
            </div>
          </div>
        )}
        <div className={`rounded-xl border border-gray-200 dark:border-dark-700 p-4 bg-white dark:bg-dark-900 shadow-inner ${imagePreview ? 'lg:w-1/3' : 'lg:flex-1'}`}>
          <div className="relative w-full mx-auto" style={{ maxWidth: boardWidth }}>
            <Chessboard
              id="board-editor"
              position={boardToFen(board)}
              boardOrientation={orientation}
              arePiecesDraggable={false}
              onSquareClick={handleSquareClick}
              customSquareStyles={squareStyles}
              customDarkSquareStyle={{
                backgroundColor: '#779952',
              }}
              customLightSquareStyle={{
                backgroundColor: '#edeed1',
              }}
              customBoardStyle={{
                borderRadius: '0.75rem',
                boxShadow:
                  '0 35px 60px -30px rgba(15,23,42,0.6), 0 8px 12px -6px rgba(15,23,42,0.4)',
              }}
              animationDuration={150}
            />
          </div>
          <p className="mt-3 text-xs text-gray-500 dark:text-dark-400">
            {selectedSquare
              ? `Selected ${selectedSquare}`
              : 'Tap a piece to select it before moving.'}
          </p>
        </div>

        <div className={`space-y-4 ${imagePreview ? 'lg:w-1/3' : 'lg:w-80'}`}>
          <div className="space-y-3">
            <p className="text-sm font-semibold text-gray-600 dark:text-dark-300">
              Tools
            </p>
            <div className="flex gap-2">
              <button
                type="button"
                className={toolButtonClass('move')}
                onClick={() => setActiveTool('move')}
              >
                Move
              </button>
              <button
                type="button"
                className={toolButtonClass('palette')}
                onClick={() => setActiveTool('palette')}
              >
                Palette
              </button>
              <button
                type="button"
                className={toolButtonClass('erase')}
                onClick={() => setActiveTool('erase')}
              >
                Erase
              </button>
            </div>
          </div>

          {activeTool === 'palette' && (
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm font-medium text-gray-600 dark:text-dark-300">
                <span>Piece palette</span>
                <span className="text-xs text-gray-400">Click to select</span>
              </div>
              <div className="grid grid-cols-6 gap-2">
                {palettePieces.map((piece) => {
                  const label = pieceLabelMap[piece] ?? piece
                  // Create a minimal board with just this piece on a8 (top-left)
                  const position = `${piece}7/8/8/8/8/8/8/8`
                  return (
                    <button
                      key={piece}
                      type="button"
                      onClick={() => setPalettePiece(piece)}
                      aria-label={`Place white ${label}`}
                      className={`rounded-lg border transition relative overflow-hidden ${palettePiece === piece
                          ? 'border-primary-500 ring-2 ring-primary-400 bg-primary-50 dark:bg-primary-900/20'
                          : 'border-gray-200 dark:border-dark-600 bg-gray-50 dark:bg-dark-700 hover:border-gray-300 dark:hover:border-dark-500'
                        }`}
                      style={{ width: '48px', height: '48px' }}
                    >
                      <div
                        style={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          width: '320px',
                          height: '320px',
                        }}
                      >
                        <Chessboard
                          position={position}
                          boardWidth={320}
                          arePiecesDraggable={false}
                          showBoardNotation={false}
                          customBoardStyle={{
                            borderRadius: '0',
                          }}
                          customDarkSquareStyle={{
                            backgroundColor: 'transparent',
                          }}
                          customLightSquareStyle={{
                            backgroundColor: 'transparent',
                          }}
                        />
                      </div>
                    </button>
                  )
                })}
                {palettePieces.map((piece) => {
                  const blackPiece = piece.toLowerCase()
                  const label = pieceLabelMap[piece] ?? piece
                  // Create a minimal board with just this piece on a8 (top-left)
                  const position = `${blackPiece}7/8/8/8/8/8/8/8`
                  return (
                    <button
                      key={`black-${piece}`}
                      type="button"
                      onClick={() => setPalettePiece(blackPiece)}
                      aria-label={`Place black ${label}`}
                      className={`rounded-lg border transition relative overflow-hidden ${palettePiece === blackPiece
                          ? 'border-primary-500 ring-2 ring-primary-400 bg-primary-50 dark:bg-primary-900/20'
                          : 'border-gray-200 dark:border-dark-600 bg-gray-50 dark:bg-dark-700 hover:border-gray-300 dark:hover:border-dark-500'
                        }`}
                      style={{ width: '48px', height: '48px' }}
                    >
                      <div
                        style={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          width: '320px',
                          height: '320px',
                        }}
                      >
                        <Chessboard
                          position={position}
                          boardWidth={320}
                          arePiecesDraggable={false}
                          showBoardNotation={false}
                          customBoardStyle={{
                            borderRadius: '0',
                          }}
                          customDarkSquareStyle={{
                            backgroundColor: 'transparent',
                          }}
                          customLightSquareStyle={{
                            backgroundColor: 'transparent',
                          }}
                        />
                      </div>
                    </button>
                  )
                })}
              </div>
            </div>
          )}

          <div className="space-y-2">
            <div className="flex items-center justify-between text-sm font-semibold text-gray-600 dark:text-dark-300">
              <span>History</span>
              <span className="text-xs text-gray-400">Unlimited redo</span>
            </div>
            <div className="flex gap-2">
              <Button
                variant="ghost"
                size="sm"
                disabled={!historyRef.current.canUndo}
                onClick={handleUndo}
                className="flex-1"
              >
                Undo
              </Button>
              <Button
                variant="ghost"
                size="sm"
                disabled={!historyRef.current.canRedo}
                onClick={handleRedo}
                className="flex-1"
              >
                Redo
              </Button>
            </div>
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <label className="flex items-center gap-2 text-sm text-gray-600 dark:text-dark-300">
                <input
                  type="checkbox"
                  checked={validatePosition}
                  onChange={(event) => setValidatePosition(event.target.checked)}
                  className="accent-primary-500"
                />
                Validate position
              </label>
              {!validation.valid && (
                <span className="text-xs text-yellow-500 font-semibold">
                  Invalid
                </span>
              )}
            </div>
            {validatePosition && !validation.valid && (
              <div className="text-xs text-yellow-500 bg-yellow-500/10 border border-yellow-500/30 rounded-md p-3 space-y-1">
                {validation.messages.map((message) => (
                  <p key={message}>{message}</p>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </motion.div>
  )
}

export default BoardEditor
